import React, { useState } from "react";
import {
  Rocket, UserPlus, CreditCard, Pizza, ListOrdered, Banknote, Smartphone,
  Gift, Globe, ShieldAlert, ChevronDown, ChevronUp, FileText, Sparkles,
} from "lucide-react";

/**
 * Restaurateur Guide — A-Z onboarding & feature reference.
 * Pure presentational component, fully i18n-driven via `t.guide.*`.
 */
export default function GuideTab({ t }) {
  const sections = [
    { id: "quickstart", icon: <Rocket className="w-5 h-5" />, color: "yellow", titleKey: "quickstart" },
    { id: "register", icon: <UserPlus className="w-5 h-5" />, color: "cyan", titleKey: "register" },
    { id: "subscription", icon: <CreditCard className="w-5 h-5" />, color: "violet", titleKey: "subscription" },
    { id: "menu", icon: <Pizza className="w-5 h-5" />, color: "magenta", titleKey: "menu" },
    { id: "orders", icon: <ListOrdered className="w-5 h-5" />, color: "cyan", titleKey: "orders" },
    { id: "payments", icon: <Banknote className="w-5 h-5" />, color: "yellow", titleKey: "payments" },
    { id: "pickup", icon: <Smartphone className="w-5 h-5" />, color: "magenta", titleKey: "pickup" },
    { id: "loyalty", icon: <Gift className="w-5 h-5" />, color: "violet", titleKey: "loyalty" },
    { id: "google", icon: <Globe className="w-5 h-5" />, color: "cyan", titleKey: "google" },
    { id: "commissions", icon: <ShieldAlert className="w-5 h-5" />, color: "yellow", titleKey: "commissions" },
  ];

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="guide-tab">
      {/* Sidebar TOC */}
      <aside className="lg:col-span-3 lg:sticky lg:top-6 lg:self-start">
        <div className="card-neon p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-3">
            {t.guide?.tocTitle || "Indice"}
          </div>
          <nav className="flex flex-col gap-1">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#guide-${s.id}`}
                className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg hover:bg-deep/60 text-zinc-300 hover:text-neon-cyan transition"
                data-testid={`guide-toc-${s.id}`}
              >
                <span className="text-zinc-500">{s.icon}</span>
                <span>{t.guide?.[s.titleKey]?.title || s.titleKey}</span>
              </a>
            ))}
            <a
              href="#guide-faq"
              className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg hover:bg-deep/60 text-zinc-300 hover:text-neon-cyan transition mt-2 pt-2 border-t border-line"
              data-testid="guide-toc-faq"
            >
              <Sparkles className="w-5 h-5 text-zinc-500" />
              <span>{t.guide?.faqTitle || "FAQ"}</span>
            </a>
          </nav>
        </div>
      </aside>

      {/* Content */}
      <div className="lg:col-span-9 space-y-6">
        <header className="card-neon p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">
            FOODGO · {t.guide?.kicker || "GUIDA RISTORATORE"}
          </div>
          <h2 className="font-heading text-3xl mt-1 mb-2">{t.guide?.heroTitle || "Tutto quello che ti serve, in un posto solo"}</h2>
          <p className="text-zinc-400 text-sm max-w-2xl">
            {t.guide?.heroIntro || "Da zero al primo ordine in pochi minuti. Sezioni dettagliate, FAQ pratiche, suggerimenti per aumentare le vendite."}
          </p>
        </header>

        {sections.map((s) => (
          <Section key={s.id} id={`guide-${s.id}`} icon={s.icon} color={s.color} data={t.guide?.[s.titleKey]} />
        ))}

        {/* FAQ */}
        <FaqSection t={t} />
      </div>
    </div>
  );
}

function Section({ id, icon, color, data }) {
  if (!data) return null;
  const ringCls = color === "cyan" ? "bg-[#00E5FF] text-[#06061a]"
    : color === "yellow" ? "bg-[#FFD600] text-[#06061a]"
    : color === "violet" ? "bg-[#7C3AED] text-white"
    : "bg-[#FF2EC8] text-white";
  return (
    <section id={id} className="card-neon p-6 scroll-mt-24" data-testid={`guide-section-${id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ringCls}`}>{icon}</div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">{data.kicker || ""}</div>
          <h3 className="font-heading text-xl">{data.title}</h3>
        </div>
      </div>
      {data.intro && <p className="text-sm text-zinc-300 mb-4 max-w-3xl">{data.intro}</p>}
      {Array.isArray(data.steps) && data.steps.length > 0 && (
        <ol className="space-y-3">
          {data.steps.map((step, i) => (
            <li key={i} className="flex gap-3 items-start" data-testid={`guide-step-${id}-${i}`}>
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-deep border border-neon-cyan text-neon-cyan flex items-center justify-center text-xs font-bold font-mono">{i + 1}</span>
              <div className="flex-1 min-w-0">
                {typeof step === "string" ? (
                  <div className="text-sm text-zinc-200 leading-relaxed">{step}</div>
                ) : (
                  <>
                    <div className="text-sm text-zinc-100 font-semibold leading-relaxed">{step.title}</div>
                    {step.body && <div className="text-sm text-zinc-400 leading-relaxed mt-0.5">{step.body}</div>}
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
      {Array.isArray(data.tips) && data.tips.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-mono mb-1.5">💡 {data.tipsLabel || "Consigli"}</div>
          <ul className="space-y-1">
            {data.tips.map((tip, i) => (
              <li key={i} className="text-xs text-amber-100/90 flex gap-2"><span>•</span><span>{tip}</span></li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function FaqSection({ t }) {
  const [openIdx, setOpenIdx] = useState(-1);
  const items = Array.isArray(t.guide?.faq) ? t.guide.faq : [];
  return (
    <section id="guide-faq" className="card-neon p-6 scroll-mt-24" data-testid="guide-section-faq">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#7C3AED] text-white">
          <FileText className="w-5 h-5" />
        </div>
        <h3 className="font-heading text-xl">{t.guide?.faqTitle || "Domande frequenti"}</h3>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => {
          const open = openIdx === i;
          return (
            <div key={i} className="rounded-xl border border-line bg-deep/40 overflow-hidden">
              <button
                onClick={() => setOpenIdx(open ? -1 : i)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-zinc-100 hover:bg-deep/60 transition"
                data-testid={`guide-faq-q-${i}`}
              >
                <span>{item.q}</span>
                {open ? <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
              </button>
              {open && (
                <div className="px-4 pb-4 text-sm text-zinc-300 leading-relaxed border-t border-line" data-testid={`guide-faq-a-${i}`}>
                  {item.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
